import React, { useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { toast } from 'sonner';
import { Label } from '@artstorefronts/ui';
import { MediaDropRegion, presignedS3Uploader, type UploadItem } from '@arthelper/ui';
import { useTranslation } from 'react-i18next';

import {
  UPDATE_PROFILE,
  UPLOAD_WATERMARK,
  REMOVE_WATERMARK,
  ADMIN_UPLOAD_WATERMARK,
  ADMIN_REMOVE_WATERMARK,
  ADMIN_UPDATE_PROFILE_COMPREHENSIVE,
} from '../../graphql/mutations';

interface WatermarkUploaderProps {
  watermarkUrl?: string | null;
  onWatermarkChange?: () => void;
  refetchProfile?: () => Promise<void>;
  isAdminMode?: boolean;
  targetUserId?: string;
  profileId?: string;
}

export const WatermarkUploader: React.FC<WatermarkUploaderProps> = ({
  watermarkUrl,
  onWatermarkChange,
  refetchProfile,
  isAdminMode = false,
  targetUserId,
  profileId,
}) => {
  const { t } = useTranslation('settings');
  const [updateProfile] = useMutation(UPDATE_PROFILE);
  const [adminUpdateProfileComprehensive] = useMutation(ADMIN_UPDATE_PROFILE_COMPREHENSIVE);
  const [uploadWatermark] = useMutation(UPLOAD_WATERMARK);
  const [adminUploadWatermark] = useMutation(ADMIN_UPLOAD_WATERMARK);
  const [removeWatermark] = useMutation(REMOVE_WATERMARK);
  const [adminRemoveWatermark] = useMutation(ADMIN_REMOVE_WATERMARK);

  const validate = async (file: File) => {
    if (file.type !== 'image/png') return t('profile.watermark.invalid-format');
    if (file.size > 2 * 1024 * 1024) return t('profile.watermark.file-too-large');
    const objectUrl = URL.createObjectURL(file);
    try {
      const hasTransparency = await new Promise<boolean>((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          try {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            if (!ctx) throw new Error('Could not create canvas context');
            ctx.drawImage(img, 0, 0);
            const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
            resolve(pixels.some((value, index) => index % 4 === 3 && value < 255));
          } catch (error) {
            reject(error);
          }
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = objectUrl;
      });
      return hasTransparency ? null : t('profile.watermark.no-transparency');
    } catch {
      return t('profile.watermark.validate-error');
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  };

  const uploader = useMemo(
    () =>
      presignedS3Uploader<string>({
        presign: async () => {
          const signed =
            isAdminMode && targetUserId
              ? (await adminUploadWatermark({ variables: { userId: targetUserId } })).data?.adminUploadWatermark
              : (await uploadWatermark()).data?.uploadWatermark;
          if (!signed?.url || !signed?.key) throw new Error('Failed to get upload URL');
          return { url: signed.url, key: signed.key, contentType: 'image/png' };
        },
        finalize: async (_file, signed) => {
          const url = signed.url.split('?')[0];
          if (isAdminMode && profileId) {
            await adminUpdateProfileComprehensive({
              variables: { input: { profileId, watermarkUrl: url, watermarkCustomKey: signed.key } },
            });
          } else {
            await updateProfile({ variables: { input: { watermarkUrl: url, watermarkCustomKey: signed.key } } });
          }
          return url;
        },
      }),
    [
      adminUpdateProfileComprehensive,
      adminUploadWatermark,
      isAdminMode,
      profileId,
      targetUserId,
      updateProfile,
      uploadWatermark,
    ]
  );

  const existing = useMemo(
    () =>
      watermarkUrl
        ? [
            {
              id: `watermark:${watermarkUrl}`,
              url: watermarkUrl,
              kind: 'image' as const,
              name: t('profile.watermark.alt-text'),
            },
          ]
        : [],
    [watermarkUrl, t]
  );

  const handleRemoveWatermark = async () => {
    try {
      // Remove watermark - use admin mutation if in admin mode
      if (isAdminMode && targetUserId) {
        await adminRemoveWatermark({
          variables: { userId: targetUserId },
        });
      } else {
        await removeWatermark();
        await updateProfile({
          variables: {
            input: {
              watermarkUrl: null,
              watermarkCustomKey: null,
            },
          },
        });
      }

      // Refresh profile data
      if (refetchProfile) {
        await refetchProfile();
      }

      // Notify parent component
      if (onWatermarkChange) {
        onWatermarkChange();
      }

      toast.success(t('profile.watermark.remove-success'));
    } catch (error) {
      console.error('Error removing watermark:', error);
      toast.error(
        t('profile.watermark.remove-error', { error: error instanceof Error ? error.message : 'Unknown error' })
      );
    }
  };

  return (
    <div className="space-y-4 border border-gray-200 rounded-md p-4 bg-gray-50">
      <div>
        <Label htmlFor="watermarkFile" className="mb-2 block text-sm">
          {t('profile.watermark.custom-label')}
        </Label>
        <p className="text-xs text-gray-500 mb-2">{t('profile.watermark.custom-description')}</p>
        <MediaDropRegion
          width="100%"
          tone="neutral"
          prompt={t('profile.watermark.custom-label')}
          hint="PNG · 2 MB"
          accept={['image/png']}
          maxSizeMB={2}
          multiple={false}
          existing={existing}
          validate={validate}
          uploader={uploader}
          onItemComplete={async () => {
            await refetchProfile?.();
            onWatermarkChange?.();
            toast.success(t('profile.watermark.upload-success'));
          }}
          onItemError={(_, error) => toast.error(t('profile.watermark.upload-error', { error: error.message }))}
          onRemove={(item: UploadItem) => {
            if (item.phase === 'done') void handleRemoveWatermark();
          }}
        />
      </div>

      {watermarkUrl && (
        <div className="flex items-center gap-4 p-3 bg-white rounded border border-gray-200">
          <div className="flex-shrink-0 bg-black p-2 rounded">
            <img
              src={watermarkUrl}
              alt={t('profile.watermark.alt-text')}
              className="h-16 w-auto max-w-[200px] object-contain"
            />
          </div>
          <div className="flex-1">
            <p className="text-sm text-gray-700 mb-1">{t('profile.watermark.active-status')}</p>
            <p className="text-xs text-gray-500">{t('profile.watermark.active-description')}</p>
          </div>
        </div>
      )}
    </div>
  );
};
